import { z } from 'zod';

// =====================================================================
// Plan 4B — Actions, Sequences, Triggers
// =====================================================================

export const ActionCategorySchema = z.enum([
  'io',
  'motion',
  'comms',
  'alarm',
  'maintenance',
  'fix',
  'system',
]);

export const ParamTypeSchema = z.enum([
  'bool',
  'int',
  'float',
  'string',
  'tag_ref',
  'enum',
  'int_list',
  'float_list',
  'string_list',
]);

export const ParamSpecSchema = z
  .object({
    name: z.string().min(1),
    type: ParamTypeSchema,
    default: z.string().nullable(),
    required: z.boolean(),
    options: z.array(z.string()),
    description: z.string().nullable(),
  })
  .superRefine((spec, ctx) => {
    if (spec.default === null) return;
    if (!spec.type.endsWith('_list')) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(spec.default);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['default'],
        message: `default for ${spec.type} must be valid JSON`,
      });
      return;
    }
    if (!Array.isArray(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['default'],
        message: `default for ${spec.type} must be a JSON array`,
      });
      return;
    }
    const innerCheck =
      spec.type === 'int_list'
        ? (v: unknown) => Number.isInteger(v)
        : spec.type === 'float_list'
          ? (v: unknown) => typeof v === 'number' && Number.isFinite(v)
          : /* string_list */ (v: unknown) => typeof v === 'string';
    for (let i = 0; i < parsed.length; i++) {
      if (!innerCheck(parsed[i])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['default'],
          message: `default[${i}] does not match inner type for ${spec.type}`,
        });
      }
    }
  });

export const RelayActionSchema = z.enum(['on', 'off', 'toggle']);
export const LogLevelSchema = z.enum(['debug', 'info', 'warn', 'error']);

export const ExpressionSchema = z.object({ source: z.string() });
export const TagRefSchema = z.object({ tag_id: z.string().min(1) });

const SystemOpSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('delay'), ms: ExpressionSchema }),
  z.object({ op: z.literal('set_tag'), tag: TagRefSchema, value: ExpressionSchema }),
  z.object({ op: z.literal('log'), level: LogLevelSchema, message: ExpressionSchema }),
  z.object({ op: z.literal('emit_event'), name: z.string(), payload: ExpressionSchema }),
  z.object({
    op: z.literal('wait_for_condition'),
    condition: ExpressionSchema,
    timeout_ms: ExpressionSchema.nullable(),
  }),
  z.object({ op: z.literal('health_check'), motors_only: z.boolean() }),
  // P11 (fh-iot): rotate the caller's named params. The action body reads
  // its caller's `param_overrides.shiftKeys` at runtime via the supplied
  // expression — typically `${param:shiftKeys}`.
  z.object({ op: z.literal('shift_param'), params: ExpressionSchema }),
  // P11 (fh-iot): invoke a sub-sequence as a side-effecting system op
  // (separate from `StepCallSchema.sub_sequence`, which is a hard nested call).
  z.object({
    op: z.literal('next_sequence'),
    sequence_ref: z.lazy(() => SequenceRefSchema),
    param_overrides: z.record(z.string(), ExpressionSchema),
  }),
  // P11 (fh-iot): assert that another sequence's checks would pass right now.
  z.object({
    op: z.literal('verify_sequence'),
    sequence_ref: z.lazy(() => SequenceRefSchema),
    // Reuses the shared CheckSchema (declared below). Forward-reference via
    // z.lazy to avoid a hoisting issue.
    checks: z.array(z.lazy(() => CheckSchema)),
  }),
]);

export const InstructionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('relay'),
    channel: TagRefSchema,
    action: RelayActionSchema,
    timeout_ms: ExpressionSchema.nullable(),
  }),
  z.object({
    kind: z.literal('stepper'),
    motor: z.object({ motor_id: z.string().min(1) }),
    steps: ExpressionSchema,
    speed: ExpressionSchema,
    accel: ExpressionSchema,
    endpoint_ls: TagRefSchema.nullable(),
    skip_count: ExpressionSchema.nullable(),
  }),
  z.object({
    kind: z.literal('dc_motor'),
    motor: z.object({ motor_id: z.string().min(1) }),
    direction: ExpressionSchema,
    duty_cycle: ExpressionSchema,
    time_ms: ExpressionSchema,
    endpoint_ls: TagRefSchema.nullable(),
    endpoint_ls2: TagRefSchema.nullable(),
    repetition: ExpressionSchema.nullable(),
    reverse: z.boolean(),
  }),
  z.object({
    kind: z.literal('belt'),
    motor: z.object({ motor_id: z.string().min(1) }),
    steps: ExpressionSchema,
    speed: ExpressionSchema,
    accel: ExpressionSchema,
    endpoint_ls: TagRefSchema.nullable(),
    endpoint_ls2: TagRefSchema.nullable(),
    repetition: ExpressionSchema.nullable(),
  }),
  z.object({
    kind: z.literal('pump'),
    pump: TagRefSchema,
    volume_ml: ExpressionSchema,
    flow_sensor: TagRefSchema,
    slot_index: ExpressionSchema.nullable(),
    tube_type: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('sensor_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    start_register: z.number().int().min(0).max(65535),
    register_count: z.number().int().min(0).max(65535),
    slot_index: z.number().int().min(0).max(255).nullable(),
  }),
  // Sensor reads that decode into a tag — mirror fh-iot's ts-rs Instruction.
  z.object({
    kind: z.literal('mlx90614_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    field: z.enum(['ambient', 'object']),
    target_tag: TagRefSchema,
  }),
  z.object({
    kind: z.literal('rfid_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    target_tag: TagRefSchema,
    expected_uid: ExpressionSchema.nullable(),
  }),
  z.object({
    kind: z.literal('load_cell_rs485_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    target_tag: TagRefSchema,
  }),
  z.object({
    kind: z.literal('vl53_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    target_tag: TagRefSchema,
  }),
  z.object({
    kind: z.literal('adc_loop_read'),
    sensor: z.object({ sensor_id: z.string().min(1) }),
    field: z.enum(['raw', 'engineering']),
    target_tag: TagRefSchema,
  }),
  z.object({
    kind: z.literal('induction'),
    relay: TagRefSchema,
    temperature_sensor: TagRefSchema,
    min_temp: ExpressionSchema,
    max_temp: ExpressionSchema,
    action: RelayActionSchema,
  }),
  z.object({
    kind: z.literal('system'),
    op: SystemOpSchema,
  }),
]);

export const PreconditionPolicySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('skip') }),
  z.object({ kind: z.literal('pause') }),
  z.object({
    kind: z.literal('retry'),
    interval_ms: z.number().int().nonnegative(),
    max_attempts: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('run_fix'),
    fix_action_id: z.string().min(1),
    max_attempts: z.number().int().nonnegative(),
  }),
]);

export const CheckSchema = z.object({
  expression: ExpressionSchema,
  on_fail: PreconditionPolicySchema,
  description: z.string().nullable(),
});

export const PreconditionsSchema = z.object({
  checks: z.array(CheckSchema),
});

export const TagWriteSchema = z.object({
  tag: TagRefSchema,
  value: ExpressionSchema,
});

export const StateUpdatesSchema = z.object({
  before: z.array(TagWriteSchema),
  on_pass: z.array(TagWriteSchema),
  on_fail: z.array(TagWriteSchema),
});

export const ActionSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().nullable(),
  name: z.string().min(1),
  category: ActionCategorySchema,
  description: z.string().nullable(),
  params: z.array(ParamSpecSchema),
  preconditions: PreconditionsSchema,
  instruction: InstructionSchema,
  state_updates: StateUpdatesSchema,
  on_demand: z.boolean(),
  // FK to Subsystem doc (Plan 17). Nullable for global/system actions that
  // don't belong to any subsystem.
  subsystem_id: z.string().nullable(),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateActionSchema = z.object({
  name: z.string().min(1, 'Name required'),
  category: ActionCategorySchema,
  description: z.string().nullable(),
  params: z.array(ParamSpecSchema),
  preconditions: PreconditionsSchema,
  instruction: InstructionSchema,
  state_updates: StateUpdatesSchema,
  on_demand: z.boolean(),
});

export const UpdateActionSchema = z.object({
  name: z.string().min(1).optional(),
  category: ActionCategorySchema.optional(),
  description: z.string().nullable().optional(),
  params: z.array(ParamSpecSchema).optional(),
  preconditions: PreconditionsSchema.optional(),
  instruction: InstructionSchema.optional(),
  state_updates: StateUpdatesSchema.optional(),
  on_demand: z.boolean().optional(),
  subsystem_id: z.string().nullable().optional(),
});

export const CyclesSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('once') }),
  z.object({ kind: z.literal('n'), count: z.number().int().nonnegative() }),
  z.object({ kind: z.literal('forever') }),
  z.object({
    kind: z.literal('for_each'),
    bindings: z
      .array(
        z.object({
          list_param: z.string().min(1),
          item_var: z.string().min(1),
        }),
      )
      .min(1),
  }),
]);

export const FailurePolicySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('abort_remaining') }),
  z.object({ kind: z.literal('continue_remaining') }),
  z.object({ kind: z.literal('run_fix_sequence'), sequence_id: z.string().min(1) }),
]);

export const RetryPolicySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('linear'),
    interval_ms: z.number().int().nonnegative(),
    max_attempts: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('exponential'),
    initial_ms: z.number().int().nonnegative(),
    max_ms: z.number().int().nonnegative(),
    max_attempts: z.number().int().nonnegative(),
  }),
]);

export const ActionRefSchema = z.object({
  project_id: z.string().nullable(),
  action_name: z.string().min(1),
});

export const SequenceRefSchema = z.object({
  project_id: z.string().min(1),
  sequence_name: z.string().min(1),
  version: z.number().int().nullable(),
});

export const StepCallSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('action'), action_ref: ActionRefSchema }),
  z.object({ kind: z.literal('sub_sequence'), sequence_ref: SequenceRefSchema }),
]);

// A branch directive applied to a step on its terminal outcome. Currently the
// only variant is `skip_set` — skip the listed downstream step ids. Modelled as
// a discriminated union (on `kind`) so future branch kinds slot in cleanly.
export const StepBranchSchema = z.object({
  kind: z.literal('skip_set'),
  step_ids: z.array(z.string()),
});

export const SequenceStepSchema = z.object({
  id: z.string().min(1),
  call: StepCallSchema,
  depends: z.array(z.string()),
  param_overrides: z.record(z.string(), ExpressionSchema),
  timeout_ms: z.number().int().nullable(),
  retry: RetryPolicySchema,
  label: z.string().nullable(),
  // Branch applied when this step reaches `Done`; null = no branch. Default-null
  // (serde-default mirror) so sequences stored before this field still parse.
  on_success: StepBranchSchema.nullable().default(null),
  // Branch applied when this step is terminally `Failed`; null = no branch.
  on_failure: StepBranchSchema.nullable().default(null),
});

// Plan 16-A v2: persisted node positions for the Flow canvas.
// `node_positions` keys are SequenceStep ids; values are { x, y } in canvas coords.
// Optional + nullable so existing sequences (pre-this-field) parse cleanly.
export const SequenceLayoutSchema = z.object({
  node_positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })),
});

// Cross-field refine: when cycles.kind === 'for_each', validate that each
// binding's list_param refers to an existing list-typed param, that item_vars
// are unique, and (when all referenced defaults are present and parseable) that
// the default array lengths match across bindings (zip semantics).
//
// `data` is typed loosely (`unknown` then narrowed) so the same function can be
// chained onto SequenceSchema, CreateSequenceSchema, and UpdateSequenceSchema
// — the latter has params/cycles as optional. With `exactOptionalPropertyTypes`,
// matching all three exactly via a single typed shape isn't tractable.
const SEQUENCE_FOR_EACH_REFINE = (data: unknown, ctx: z.RefinementCtx) => {
  const d = data as {
    params?: Array<{ name: string; type: string; default: string | null }>;
    cycles?: { kind: string } & Record<string, unknown>;
  };
  if (!d.cycles) return;
  // PATCH semantics: if cycles is being updated to for_each, params must come along.
  if (d.cycles.kind === 'for_each' && !d.params) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['params'],
      message: 'for_each cycles require params to be supplied alongside in the same update',
    });
    return;
  }
  if (!d.params) return; // not for_each — params optional
  if (d.cycles.kind !== 'for_each') return;
  const bindings = d.cycles.bindings as Array<{ list_param: string; item_var: string }>;
  const paramByName = new Map(d.params.map((p) => [p.name, p]));

  const seenItemVars = new Set<string>();
  const referencedDefaultLengths: number[] = [];
  let allDefaultsPresent = true;

  for (const [i, b] of bindings.entries()) {
    const p = paramByName.get(b.list_param);
    if (!p) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycles', 'bindings', i, 'list_param'],
        message: `binding.list_param '${b.list_param}' not found in params`,
      });
      continue;
    }
    if (!p.type.endsWith('_list')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycles', 'bindings', i, 'list_param'],
        message: `binding.list_param '${b.list_param}' must be a list type (got '${p.type}')`,
      });
      continue;
    }
    if (seenItemVars.has(b.item_var)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycles', 'bindings', i, 'item_var'],
        message: `duplicate item_var '${b.item_var}'`,
      });
    }
    seenItemVars.add(b.item_var);

    if (p.default === null) {
      allDefaultsPresent = false;
    } else {
      try {
        const arr = JSON.parse(p.default);
        if (Array.isArray(arr)) referencedDefaultLengths.push(arr.length);
      } catch {
        // ParamSpec refine already flagged invalid JSON; skip length check.
        allDefaultsPresent = false;
      }
    }
  }

  if (allDefaultsPresent && referencedDefaultLengths.length > 1) {
    const first = referencedDefaultLengths[0];
    if (referencedDefaultLengths.some((n) => n !== first)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycles', 'bindings'],
        message: `for_each bindings reference list defaults with mismatched lengths (${referencedDefaultLengths.join(', ')})`,
      });
    }
  }
};

export const SequenceSchema = z
  .object({
    id: z.string().min(1),
    project_id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    version: z.number().int().nonnegative(),
    params: z.array(ParamSpecSchema),
    preconditions: PreconditionsSchema,
    steps: z.array(SequenceStepSchema),
    cycles: CyclesSchema,
    on_failure: FailurePolicySchema,
    max_runtime_ms: z.number().int().nullable(),
    layout: SequenceLayoutSchema.nullable(),
    // FK to Subsystem doc (Plan 17). Nullable — sequences with no assigned subsystem.
    subsystem_id: z.string().nullable(),
    archived_at_ms: z.number().int().nullable(),
    created_at_ms: z.number().int().nonnegative(),
    updated_at_ms: z.number().int().nonnegative(),
  })
  .superRefine(SEQUENCE_FOR_EACH_REFINE);

export const CreateSequenceSchema = z
  .object({
    name: z.string().min(1, 'Name required'),
    description: z.string().nullable(),
    params: z.array(ParamSpecSchema),
    preconditions: PreconditionsSchema,
    steps: z.array(SequenceStepSchema),
    cycles: CyclesSchema,
    on_failure: FailurePolicySchema,
    max_runtime_ms: z.number().int().nullable(),
  })
  .superRefine(SEQUENCE_FOR_EACH_REFINE);

export const UpdateSequenceSchema = z
  .object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    params: z.array(ParamSpecSchema).optional(),
    preconditions: PreconditionsSchema.optional(),
    steps: z.array(SequenceStepSchema).optional(),
    cycles: CyclesSchema.optional(),
    on_failure: FailurePolicySchema.optional(),
    max_runtime_ms: z.number().int().nullable().optional(),
    // Plan 16-A v2: persisted node layout from the Flow canvas editor.
    layout: SequenceLayoutSchema.nullable().optional(),
    subsystem_id: z.string().nullable().optional(),
  })
  .superRefine(SEQUENCE_FOR_EACH_REFINE);

export const TriggerPrioritySchema = z.enum(['high', 'medium', 'low', 'info']);
export const TriggerEdgeSchema = z.enum(['rising_edge', 'falling_edge', 'level_high', 'level_low']);

export const TriggerTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('action'),
    action_ref: ActionRefSchema,
    param_overrides: z.record(z.string(), z.string()),
  }),
  z.object({
    kind: z.literal('sequence'),
    sequence_ref: SequenceRefSchema,
    param_overrides: z.record(z.string(), z.string()),
  }),
]);

export const TriggerSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  priority: TriggerPrioritySchema,
  condition: ExpressionSchema,
  edge: TriggerEdgeSchema,
  target: TriggerTargetSchema,
  // Preconditions evaluated on master board before firing the target.
  // If any check fails, the configured on_fail policy applies; the trigger
  // does not invoke its target.
  preconditions: PreconditionsSchema,
  // Recovery policy if the target action/sequence fails.
  on_failure: FailurePolicySchema,
  require_ack: z.boolean(),
  log: z.boolean(),
  notify_channels: z.array(z.string()),
  enabled: z.boolean(),
  debounce_ms: z.number().int().nullable(),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateTriggerSchema = z.object({
  name: z.string().min(1, 'Name required'),
  priority: TriggerPrioritySchema,
  condition: ExpressionSchema,
  edge: TriggerEdgeSchema,
  target: TriggerTargetSchema,
  preconditions: PreconditionsSchema,
  on_failure: FailurePolicySchema,
  require_ack: z.boolean(),
  log: z.boolean(),
  notify_channels: z.array(z.string()),
  enabled: z.boolean(),
  debounce_ms: z.number().int().nullable(),
});

export const UpdateTriggerSchema = z.object({
  name: z.string().min(1).optional(),
  priority: TriggerPrioritySchema.optional(),
  condition: ExpressionSchema.optional(),
  edge: TriggerEdgeSchema.optional(),
  target: TriggerTargetSchema.optional(),
  preconditions: PreconditionsSchema.optional(),
  on_failure: FailurePolicySchema.optional(),
  require_ack: z.boolean().optional(),
  log: z.boolean().optional(),
  notify_channels: z.array(z.string()).optional(),
  enabled: z.boolean().optional(),
  debounce_ms: z.number().int().nullable().optional(),
});

export const SequenceRunStatusSchema = z.enum([
  'pending',
  'running',
  'done',
  'failed',
  'cancelled',
]);

export const SequenceRunEventSchema = z.object({
  run_id: z.string().min(1),
  sequence_id: z.string().min(1),
  status: SequenceRunStatusSchema,
  current_step_id: z.string().nullable(),
  completed_step_ids: z.array(z.string()),
  message: z.string().nullable(),
  timestamp_ms: z.number().int().nonnegative(),
});

export type ParsedAction = z.infer<typeof ActionSchema>;
export type ParsedCreateAction = z.infer<typeof CreateActionSchema>;
export type ParsedUpdateAction = z.infer<typeof UpdateActionSchema>;
export type ParsedSequence = z.infer<typeof SequenceSchema>;
export type ParsedCreateSequence = z.infer<typeof CreateSequenceSchema>;
export type ParsedUpdateSequence = z.infer<typeof UpdateSequenceSchema>;
export type ParsedSequenceStep = z.infer<typeof SequenceStepSchema>;
export type ParsedStepBranch = z.infer<typeof StepBranchSchema>;
export type ParsedTrigger = z.infer<typeof TriggerSchema>;
export type ParsedCreateTrigger = z.infer<typeof CreateTriggerSchema>;
export type ParsedUpdateTrigger = z.infer<typeof UpdateTriggerSchema>;
export type ParsedInstruction = z.infer<typeof InstructionSchema>;
export type ParsedExpression = z.infer<typeof ExpressionSchema>;
export type ParsedTagRef = z.infer<typeof TagRefSchema>;
export type ParsedActionCategory = z.infer<typeof ActionCategorySchema>;
export type ParsedSequenceRunEvent = z.infer<typeof SequenceRunEventSchema>;
